import React, { useRef, useEffect } from 'react';

const STAR_COUNT = 1200;
const CORE_STARS = 200;
const BG_STARS = 2000;

export function GalaxyBackground() {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const shootingRef = useRef([]);
  const timeRef = useRef(0);
  const rafRef = useRef(null);
  const mouseRef = useRef({ x: -999, y: -999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // Generate galaxy stars
    const stars = [];
    const cx = 0.5, cy = 0.5;
    for (let i = 0; i < STAR_COUNT; i++) {
      const armAngle = Math.random() * Math.PI * 2;
      const dist = Math.pow(Math.random(), 0.45) * 0.6;
      const angle = armAngle + dist * 2.5;
      const spread = 0.12 * dist + 0.03;
      const ox = (Math.random() - 0.5) * spread * 2;
      const oy = (Math.random() - 0.5) * spread * 2;
      const x = cx + Math.cos(angle) * dist + ox;
      const y = cy + Math.sin(angle) * dist * 0.6 + oy;
      const brightness = Math.random();
      const colorRand = Math.random();
      let r, g, b;
      if (dist < 0.12) { r = 255; g = 230 + Math.random() * 25; b = 180 + Math.random() * 50; }
      else if (colorRand < 0.15) { r = 140 + Math.random() * 60; g = 160 + Math.random() * 60; b = 255; }
      else if (colorRand < 0.25) { r = 255; g = 160 + Math.random() * 60; b = 120 + Math.random() * 40; }
      else if (colorRand < 0.35) { r = 255; g = 240 + Math.random() * 15; b = 200 + Math.random() * 30; }
      else { r = 220 + Math.random() * 35; g = 225 + Math.random() * 30; b = 240 + Math.random() * 15; }
      stars.push({
        x, y, r, g, b,
        size: dist < 0.1 ? 0.4 + Math.random() * 2.2 : 0.2 + Math.random() * 1.5,
        baseAlpha: 0.3 + brightness * 0.7,
        twinkleSpeed: 0.5 + Math.random() * 3,
        twinkleOffset: Math.random() * Math.PI * 2,
        isBright: brightness > 0.92,
      });
    }
    for (let i = 0; i < CORE_STARS; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = Math.pow(Math.random(), 1.2) * 0.15;
      stars.push({
        x: cx + Math.cos(a) * d + (Math.random() - 0.5) * 0.04,
        y: cy + Math.sin(a) * d * 0.55 + (Math.random() - 0.5) * 0.03,
        r: 255, g: 235 + Math.random() * 20, b: 195 + Math.random() * 40,
        size: 0.3 + Math.random() * 1, baseAlpha: 0.4 + Math.random() * 0.5,
        twinkleSpeed: 1 + Math.random() * 2, twinkleOffset: Math.random() * Math.PI * 2, isBright: false,
      });
    }
    for (let i = 0; i < BG_STARS; i++) {
      /* Grid-jitter: divide screen into cells, place one star per cell with random offset */
      const cols = Math.ceil(Math.sqrt(BG_STARS * 1.6));   // wider than tall
      const rows = Math.ceil(BG_STARS / cols);
      const col = i % cols;
      const row = Math.floor(i / cols);
      if (row >= rows) continue;
      const cellW = 1 / cols;
      const cellH = 1 / rows;
      const jitterX = (Math.random() - 0.5) * cellW * 1.6;
      const jitterY = (Math.random() - 0.5) * cellH * 1.6;
      const sx = (col + 0.5) * cellW + jitterX;
      const sy = (row + 0.5) * cellH + jitterY;
      if (sx < -0.05 || sx > 1.05 || sy < -0.05 || sy > 1.05) continue;
      const colorRand = Math.random();
      let r, g, b;
      if (colorRand < 0.08) { r = 160 + Math.random() * 60; g = 170 + Math.random() * 50; b = 255; }
      else if (colorRand < 0.14) { r = 255; g = 200 + Math.random() * 40; b = 160 + Math.random() * 40; }
      else { r = 210 + Math.random() * 45; g = 215 + Math.random() * 40; b = 230 + Math.random() * 25; }
      stars.push({
        x: sx, y: sy, r, g, b,
        size: 0.3 + Math.random() * 1.0, baseAlpha: 0.2 + Math.random() * 0.45,
        twinkleSpeed: 0.3 + Math.random() * 2, twinkleOffset: Math.random() * Math.PI * 2, isBright: Math.random() > 0.96,
      });
    }
    starsRef.current = stars;

    const shootingStars = [];
    shootingRef.current = shootingStars;

    function spawnShooting() {
      if (shootingStars.length < 2 && Math.random() < 0.006) {
        const ang = -0.3 - Math.random() * 0.5;
        shootingStars.push({
          x: Math.random() * 0.6 + 0.2, y: Math.random() * 0.3,
          vx: Math.cos(ang) * (0.003 + Math.random() * 0.004),
          vy: Math.sin(ang) * -(0.003 + Math.random() * 0.004),
          life: 1, decay: 0.015 + Math.random() * 0.01, length: 40 + Math.random() * 60,
        });
      }
    }

    function render() {
      const W = canvas.width, H = canvas.height;
      const t = (timeRef.current += 0.016);
      ctx.clearRect(0, 0, W, H);

      // Core glow
      const cg = ctx.createRadialGradient(W * 0.5, H * 0.5, 0, W * 0.5, H * 0.5, W * 0.18);
      cg.addColorStop(0, 'rgba(60,40,30,0.25)');
      cg.addColorStop(0.3, 'rgba(40,25,50,0.12)');
      cg.addColorStop(0.6, 'rgba(20,15,40,0.06)');
      cg.addColorStop(1, 'transparent');
      ctx.fillStyle = cg;
      ctx.fillRect(0, 0, W, H);

      // Nebula bands
      ctx.globalCompositeOperation = 'screen';
      const n1 = ctx.createRadialGradient(W * 0.35, H * 0.45, 0, W * 0.35, H * 0.45, W * 0.25);
      n1.addColorStop(0, `rgba(80,40,120,${0.04 + Math.sin(t * 0.3) * 0.015})`);
      n1.addColorStop(1, 'transparent');
      ctx.fillStyle = n1; ctx.fillRect(0, 0, W, H);
      const n2 = ctx.createRadialGradient(W * 0.65, H * 0.55, 0, W * 0.65, H * 0.55, W * 0.22);
      n2.addColorStop(0, `rgba(30,50,100,${0.035 + Math.sin(t * 0.25 + 1) * 0.012})`);
      n2.addColorStop(1, 'transparent');
      ctx.fillStyle = n2; ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'source-over';

      // Stars
      for (const s of stars) {
        const tw = Math.sin(t * s.twinkleSpeed + s.twinkleOffset);
        const alpha = s.baseAlpha * (0.7 + tw * 0.3);
        if (alpha < 0.05) continue;
        const sx = s.x * W, sy = s.y * H;
        const sz = s.size * (0.9 + tw * 0.1);
        if (s.isBright && sz > 1) {
          ctx.globalCompositeOperation = 'screen';
          ctx.strokeStyle = `rgba(${s.r},${s.g},${s.b},${alpha * 0.3})`;
          ctx.lineWidth = 0.5;
          const spikeLen = sz * 4;
          ctx.beginPath();
          ctx.moveTo(sx - spikeLen, sy); ctx.lineTo(sx + spikeLen, sy);
          ctx.moveTo(sx, sy - spikeLen); ctx.lineTo(sx, sy + spikeLen);
          ctx.stroke();
          const gg = ctx.createRadialGradient(sx, sy, 0, sx, sy, sz * 3);
          gg.addColorStop(0, `rgba(${s.r},${s.g},${s.b},${alpha * 0.4})`);
          gg.addColorStop(1, 'transparent');
          ctx.fillStyle = gg;
          ctx.fillRect(sx - sz * 3, sy - sz * 3, sz * 6, sz * 6);
          ctx.globalCompositeOperation = 'source-over';
        }
        ctx.beginPath();
        ctx.arc(sx, sy, Math.max(sz * 0.5, 0.3), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.r},${s.g},${s.b},${alpha})`;
        ctx.fill();
      }

      // Shooting stars
      spawnShooting();
      ctx.globalCompositeOperation = 'screen';
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const ss = shootingStars[i];
        ss.x += ss.vx; ss.y -= ss.vy; ss.life -= ss.decay;
        if (ss.life <= 0) { shootingStars.splice(i, 1); continue; }
        const sx = ss.x * W, sy = ss.y * H;
        const tx = sx - ss.vx * ss.length * W;
        const ty = sy + ss.vy * ss.length * H;
        const gr = ctx.createLinearGradient(sx, sy, tx, ty);
        gr.addColorStop(0, `rgba(255,255,255,${ss.life * 0.9})`);
        gr.addColorStop(0.3, `rgba(200,210,255,${ss.life * 0.4})`);
        gr.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(tx, ty);
        ctx.strokeStyle = gr; ctx.lineWidth = 1.5; ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';

      rafRef.current = requestAnimationFrame(render);
    }
    rafRef.current = requestAnimationFrame(render);

    // Mouse light
    function onMouse(e) { mouseRef.current = { x: e.clientX, y: e.clientY }; }
    if (window.matchMedia('(hover: hover)').matches) {
      document.addEventListener('mousemove', onMouse);
    }

    return () => {
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', onMouse);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="galaxy-bg" aria-hidden="true">
      <canvas ref={canvasRef} className="galaxy-canvas" />
      {/* CSS Nebula gas clouds */}
      <div className="nebula-layer">
        <div className="gas-cloud" style={{
          width: '50vw', height: '35vh', left: '10%', top: '15%',
          background: 'radial-gradient(ellipse, rgba(60,20,90,0.18) 0%, transparent 70%)',
          '--blur': '70px', '--dur': '18s', '--delay': '0s', '--peak': '0.2',
          '--tx1': '0px', '--ty1': '0px', '--tx2': '12px', '--ty2': '-8px',
        }} />
        <div className="gas-cloud" style={{
          width: '45vw', height: '30vh', left: '50%', top: '55%',
          background: 'radial-gradient(ellipse, rgba(20,40,90,0.15) 0%, transparent 70%)',
          '--blur': '80px', '--dur': '22s', '--delay': '-5s', '--peak': '0.18',
          '--tx1': '0px', '--ty1': '0px', '--tx2': '-15px', '--ty2': '10px',
        }} />
        <div className="gas-cloud" style={{
          width: '55vw', height: '40vh', left: '25%', top: '35%',
          background: 'radial-gradient(ellipse, rgba(40,15,70,0.12) 0%, transparent 65%)',
          '--blur': '90px', '--dur': '25s', '--delay': '-10s', '--peak': '0.15',
          '--tx1': '0px', '--ty1': '0px', '--tx2': '18px', '--ty2': '6px',
        }} />
        <div className="gas-cloud" style={{
          width: '40vw', height: '25vh', left: '60%', top: '20%',
          background: 'radial-gradient(ellipse, rgba(15,50,70,0.12) 0%, transparent 70%)',
          '--blur': '65px', '--dur': '20s', '--delay': '-8s', '--peak': '0.14',
          '--tx1': '0px', '--ty1': '0px', '--tx2': '-10px', '--ty2': '-12px',
        }} />
      </div>
      <div className="vignette-overlay" />
    </div>
  );
}
